import { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import * as XLSX from 'xlsx';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';

interface MedicamentRecord {
  id: string;
  nom: string;
  created_at: string;
}

interface PreviewRow {
  id: string;
  nom: string;
}

export default function AdminMedicamentsPage() {
  const { user, role, userRoles, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [loading, setLoading] = useState(true);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [medicaments, setMedicaments] = useState<MedicamentRecord[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<{ success: boolean; message: string } | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileName, setFileName] = useState('');
  const [previewData, setPreviewData] = useState<PreviewRow[]>([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [previewError, setPreviewError] = useState('');

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/login', { replace: true });
      return;
    }
    checkAdmin();
  }, [user, authLoading]);

  const checkAdmin = async () => {
    if (role === 'super_admin' || userRoles?.includes('super_admin')) {
      setIsSuperAdmin(true);
      setLoading(false);
      loadMedicaments();
      return;
    }
    // Admin simple : pas d'accès
    navigate('/', { replace: true });
    return;
  };

  const loadMedicaments = async () => {
    try {
      const { data, error } = await supabase
        .from('medicaments')
        .select('*')
        .order('nom', { ascending: true });

      if (error) throw error;
      setMedicaments(data || []);
    } catch (err) {
      console.error('Erreur chargement médicaments:', err);
    }
  };

  const parseExcelClientSide = (file: File): Promise<string[]> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target?.result as ArrayBuffer);
          const workbook = XLSX.read(data, { type: 'array' });
          const firstSheet = workbook.SheetNames[0];
          if (!firstSheet) {
            reject(new Error('Le fichier Excel ne contient aucune feuille'));
            return;
          }
          const worksheet = workbook.Sheets[firstSheet];
          const rawRows: unknown[][] = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

          if (rawRows.length < 2) {
            reject(new Error('Le fichier est vide ou ne contient que l\'en-tête'));
            return;
          }

          const headerRow = rawRows[0] as string[];
          let nomColumnIndex = -1;

          for (let i = 0; i < headerRow.length; i++) {
            const h = (headerRow[i] || '').toString().toLowerCase().trim();
            if (h === 'nom' || h === 'médicament' || h === 'medicament' || h === 'medicaments' || h === 'médicaments' || h === 'name' || h === 'drug' || h === 'drugs') {
              nomColumnIndex = i;
              break;
            }
          }

          if (nomColumnIndex === -1) {
            reject(new Error('Colonne "nom" introuvable. Le fichier doit avoir une colonne nommée "nom", "médicament" ou "medicament". Colonnes trouvées: ' + headerRow.filter(h => h).join(', ')));
            return;
          }

          const noms: string[] = [];
          for (let i = 1; i < rawRows.length; i++) {
            const row = rawRows[i] as string[];
            const nom = (row[nomColumnIndex] || '').toString().trim();
            if (nom && nom.length > 1) {
              noms.push(nom);
            }
          }

          if (noms.length === 0) {
            reject(new Error('Aucun médicament trouvé dans le fichier'));
            return;
          }

          resolve(noms);
        } catch (err: any) {
          reject(new Error('Erreur lors de la lecture du fichier: ' + (err.message || 'Format invalide')));
        }
      };
      reader.onerror = () => reject(new Error('Impossible de lire le fichier'));
      reader.readAsArrayBuffer(file);
    });
  };

  const handleFile = (file: File) => {
    const ext = file.name.split('.').pop()?.toLowerCase();
    if (ext !== 'xlsx' && ext !== 'xls') {
      setImportResult({ success: false, message: 'Format de fichier non supporté. Veuillez utiliser un fichier .xlsx ou .xls' });
      return;
    }
    setSelectedFile(file);
    setFileName(file.name);
    setImportResult(null);
    setPreviewError('');

    parseExcelClientSide(file)
      .then((noms) => {
        const preview: PreviewRow[] = noms.map((nom, i) => ({
          id: `preview-${i}`,
          nom,
        }));
        setPreviewData(preview);
        if (preview.length === 0) {
          setPreviewError('Aucun médicament trouvé dans le fichier.');
        }
      })
      .catch((err) => {
        setPreviewError(err.message || 'Erreur lors de la lecture du fichier.');
        setPreviewData([]);
      });
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
  };

  const handleFileDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  };

  const clearFile = () => {
    setSelectedFile(null);
    setFileName('');
    setPreviewData([]);
    setImportResult(null);
    setPreviewError('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleImport = async () => {
    if (!selectedFile) return;

    setImporting(true);
    setImportResult(null);

    try {
      const noms = previewData.map((p) => p.nom);

      const { data, error } = await supabase.functions.invoke('import-medicaments', {
        body: { noms }
      });

      if (error) {
        const ctxMsg = (error as any)?.context?.body?.error || (error as any)?.message || 'Erreur inconnue';
        throw new Error(ctxMsg);
      }

      if (data?.error) {
        throw new Error(data.error);
      }

      setImportResult({
        success: true,
        message: data?.message || `${noms.length} médicament(s) importé(s) avec succès`
      });

      clearFile();
      loadMedicaments();
    } catch (err: any) {
      console.error('Erreur import:', err);
      setImportResult({ success: false, message: err.message || 'Erreur lors de l\'import' });
    } finally {
      setImporting(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      const { error } = await supabase.from('medicaments').delete().eq('id', id);
      if (error) throw error;
      setMedicaments(prev => prev.filter(m => m.id !== id));
      setShowDeleteConfirm(null);
    } catch (err) {
      console.error('Erreur suppression:', err);
      alert('Erreur lors de la suppression');
    }
  };

  const filteredMedicaments = searchTerm.trim()
    ? medicaments.filter(m => m.nom.toLowerCase().includes(searchTerm.toLowerCase()))
    : medicaments;

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <i className="ri-loader-4-line text-4xl text-teal-600 animate-spin mb-4"></i>
          <div className="text-gray-600 text-xl">Chargement...</div>
        </div>
      </div>
    );
  }

  if (!isSuperAdmin) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <Header />

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* En-tête */}
        <div className="mb-10">
          <button
            onClick={() => navigate('/admin')}
            className="flex items-center gap-2 text-gray-500 hover:text-gray-700 mb-4 transition-colors whitespace-nowrap cursor-pointer text-sm"
          >
            <i className="ri-arrow-left-line"></i>
            Retour au tableau de bord
          </button>
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Gestion des Médicaments</h1>
          <p className="text-gray-600">Importez et gérez la base de médicaments pour l'autocomplétion des prescriptions</p>
        </div>

        {/* Messages d'erreur généraux */}
        {importResult && !previewData.length && (
          <div className={`mb-6 p-4 rounded-xl text-sm ${
            importResult.success
              ? 'bg-green-50 border-2 border-green-200 text-green-700'
              : 'bg-red-50 border-2 border-red-200 text-red-700'
          }`}>
            <div className="flex items-start gap-2">
              <i className={`${importResult.success ? 'ri-checkbox-circle-line' : 'ri-error-warning-line'} mt-0.5 w-5 h-5 flex items-center justify-center`}></i>
              {importResult.message}
            </div>
          </div>
        )}

        {/* Import Section */}
        <div className="bg-white rounded-2xl shadow-xl p-6 sm:p-8 mb-8 border border-teal-100">
          <h2 className="text-xl font-bold text-gray-800 mb-2 flex items-center gap-2">
            <i className="ri-file-excel-2-line text-teal-600 text-2xl"></i>
            Importer un fichier Excel
          </h2>
          <p className="text-gray-500 text-sm mb-6">
            Téléchargez un fichier .xlsx ou .xls contenant une colonne <strong>nom</strong> (ou médicament) avec la liste des médicaments.
          </p>

          {/* Zone de drop */}
          <div
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleFileDrop}
            onClick={() => fileInputRef.current?.click()}
            className={`border-2 border-dashed rounded-2xl p-8 text-center cursor-pointer transition-all ${
              dragOver
                ? 'border-teal-500 bg-teal-50/50'
                : fileName
                  ? 'border-teal-300 hover:border-teal-500 bg-teal-50/30'
                  : 'border-teal-300 hover:border-teal-500 hover:bg-teal-50/50'
            }`}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls"
              onChange={handleFileSelect}
              className="hidden"
            />
            {fileName ? (
              <div className="space-y-1">
                <div className="w-16 h-16 bg-teal-100 rounded-2xl flex items-center justify-center mx-auto mb-2">
                  <i className="ri-file-excel-2-line text-3xl text-teal-600"></i>
                </div>
                <p className="text-lg font-semibold text-gray-900">{fileName}</p>
                <p className="text-sm text-gray-500">{previewData.length} médicament(s) détecté(s)</p>
                <button
                  onClick={(e) => { e.stopPropagation(); clearFile(); }}
                  className="mt-2 text-sm text-red-500 hover:text-red-700 cursor-pointer whitespace-nowrap inline-flex items-center gap-1"
                >
                  <i className="ri-delete-bin-line"></i>
                  Supprimer
                </button>
              </div>
            ) : (
              <div className="space-y-2">
                <div className="w-16 h-16 bg-teal-50 rounded-2xl flex items-center justify-center mx-auto mb-2">
                  <i className="ri-upload-cloud-2-line text-3xl text-teal-400"></i>
                </div>
                <p className="text-base font-semibold text-gray-700">
                  Glissez-déposez votre fichier ici
                </p>
                <p className="text-sm text-gray-500">
                  ou cliquez pour parcourir (.xlsx, .xls)
                </p>
              </div>
            )}
          </div>

          {/* Erreur de preview */}
          {previewError && (
            <div className="mt-4 p-4 bg-red-50 border-2 border-red-200 rounded-xl text-sm text-red-700">
              <div className="flex items-start gap-2">
                <i className="ri-error-warning-line mt-0.5 w-5 h-5 flex items-center justify-center"></i>
                {previewError}
              </div>
            </div>
          )}

          {/* Format attendu */}
          {!fileName && (
            <details className="mt-4">
              <summary className="text-xs text-gray-400 cursor-pointer hover:text-gray-600">Format attendu du fichier</summary>
              <div className="mt-2 p-3 bg-gray-50 rounded-lg text-xs text-gray-500 font-mono">
                <p className="font-semibold mb-1">Le fichier doit contenir une colonne avec l'un de ces en-têtes :</p>
                <ul className="list-disc list-inside space-y-0.5">
                  <li>nom</li>
                  <li>médicament / medicament</li>
                  <li>médicaments / medicaments</li>
                  <li>name / drug / drugs</li>
                </ul>
                <p className="mt-2 text-gray-400">Exemple :</p>
                <table className="mt-1 border-collapse border border-gray-300 w-full">
                  <thead>
                    <tr><th className="border border-gray-300 px-2 py-1 bg-gray-100">nom</th></tr>
                  </thead>
                  <tbody>
                    <tr><td className="border border-gray-300 px-2 py-1">Paracétamol</td></tr>
                    <tr><td className="border border-gray-300 px-2 py-1">Ibuprofène</td></tr>
                    <tr><td className="border border-gray-300 px-2 py-1">Amoxicilline</td></tr>
                  </tbody>
                </table>
              </div>
            </details>
          )}

          {/* Message résultat import (quand pas de preview) */}
          {importResult && !previewData.length && (
            <div className={`mt-4 p-4 rounded-xl text-sm ${
              importResult.success
                ? 'bg-green-50 border-2 border-green-200 text-green-700'
                : 'bg-red-50 border-2 border-red-200 text-red-700'
            }`}>
              <div className="flex items-start gap-2">
                <i className={`${importResult.success ? 'ri-checkbox-circle-line' : 'ri-error-warning-line'} mt-0.5 w-5 h-5 flex items-center justify-center`}></i>
                {importResult.message}
              </div>
            </div>
          )}
        </div>

        {/* Aperçu des données avant import */}
        {previewData.length > 0 && (
          <div className="bg-white rounded-2xl shadow-xl p-6 sm:p-8 mb-8 border border-teal-100">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                <i className="ri-eye-line text-teal-600 text-xl"></i>
                Aperçu ({previewData.length} médicaments)
              </h3>
              <button
                onClick={clearFile}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-500 hover:text-red-500 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-close-line"></i>
                Annuler
              </button>
            </div>

            <div className="max-h-80 overflow-y-auto border border-gray-200 rounded-xl mb-6">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="text-left px-4 py-3 font-semibold text-gray-700 w-16">#</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-700">Nom du médicament</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {previewData.map((row, i) => (
                    <tr key={row.id} className="hover:bg-gray-50/50 transition-colors">
                      <td className="px-4 py-2.5 text-gray-400">{i + 1}</td>
                      <td className="px-4 py-2.5">
                        <div className="flex items-center gap-2">
                          <i className="ri-capsule-line text-teal-500"></i>
                          <span className="font-medium text-gray-800">{row.nom}</span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Message résultat import */}
            {importResult && (
              <div className={`mb-4 p-4 rounded-xl text-sm ${
                importResult.success
                  ? 'bg-green-50 border-2 border-green-200 text-green-700'
                  : 'bg-red-50 border-2 border-red-200 text-red-700'
              }`}>
                <div className="flex items-start gap-2">
                  <i className={`${importResult.success ? 'ri-checkbox-circle-line' : 'ri-error-warning-line'} mt-0.5 w-5 h-5 flex items-center justify-center`}></i>
                  {importResult.message}
                </div>
              </div>
            )}

            <button
              onClick={handleImport}
              disabled={importing}
              className="w-full px-6 py-3.5 bg-gradient-to-r from-teal-600 to-teal-700 text-white rounded-xl hover:from-teal-700 hover:to-teal-800 transition-all font-semibold shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer flex items-center justify-center gap-2 text-sm"
            >
              {importing ? (
                <>
                  <i className="ri-loader-4-line animate-spin"></i>
                  Importation en cours...
                </>
              ) : (
                <>
                  <i className="ri-upload-cloud-2-line"></i>
                  Importer {previewData.length} médicament{previewData.length > 1 ? 's' : ''}
                </>
              )}
            </button>
          </div>
        )}

        {/* Liste des médicaments */}
        <div className="bg-white rounded-2xl shadow-xl p-6 sm:p-8 border border-gray-100">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
            <div>
              <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                <i className="ri-capsule-line text-teal-600 text-2xl"></i>
                Base de médicaments
              </h2>
              <p className="text-gray-500 text-sm mt-1">{medicaments.length} médicament(s) dans la base</p>
            </div>
            <div className="relative w-full sm:w-64">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Rechercher un médicament..."
                className="w-full pl-10 pr-4 py-2.5 border-2 border-gray-200 rounded-xl focus:border-teal-500 focus:outline-none transition-colors text-sm"
              />
            </div>
          </div>

          {filteredMedicaments.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <i className="ri-capsule-line text-5xl mb-4 block"></i>
              {searchTerm ? (
                <p>Aucun médicament trouvé pour "{searchTerm}"</p>
              ) : (
                <p>Aucun médicament dans la base. Importez un fichier Excel pour commencer.</p>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b-2 border-gray-100">
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Médicament</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase hidden sm:table-cell">Ajouté le</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase w-20">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredMedicaments.map((m) => (
                    <tr key={m.id} className="border-b border-gray-50 hover:bg-gray-50/50 transition-colors">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <i className="ri-capsule-line text-teal-500"></i>
                          <span className="text-sm font-medium text-gray-800">{m.nom}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500 hidden sm:table-cell">
                        {new Date(m.created_at).toLocaleDateString('fr-FR')}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {showDeleteConfirm === m.id ? (
                          <div className="flex items-center justify-end gap-1">
                            <button
                              onClick={() => handleDelete(m.id)}
                              className="px-2 py-1 bg-red-500 text-white rounded text-xs hover:bg-red-600 whitespace-nowrap cursor-pointer"
                            >
                              Confirmer
                            </button>
                            <button
                              onClick={() => setShowDeleteConfirm(null)}
                              className="px-2 py-1 bg-gray-200 text-gray-600 rounded text-xs hover:bg-gray-300 whitespace-nowrap cursor-pointer"
                            >
                              Annuler
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => setShowDeleteConfirm(m.id)}
                            className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors cursor-pointer"
                            >
                              <i className="ri-delete-bin-line"></i>
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

        <Footer />
      </div>
    </div>
  );
}